import { useCreateEvent } from "@/features/events/hooks/useCreateEvent"
import { EventType as NewEventType } from "@/features/events/types/EventType"
import { DnaTestTrackingStatus } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingStatus"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import PrimaryButton from "../../../components/buttons/PrimaryButton"
import { saveEvent } from "../../../utils/apis/user-service"
import { EventType } from "../../../utils/enums/EventType"
import { useAuthStore } from "../../../utils/stores/useAuthStore"
import { useUserStore } from "../../../utils/stores/useUserStore"

enum DotType {
    Filled = "filled",
    BlueBorder = "blue-border",
    Normal = "normal",
}

interface Props {
    status: DnaTestTrackingStatus,
}

const TrackingWidget: React.FC<Props> = ({
    status,
}) => {
    const { sendEvent } = useCreateEvent()
    const { auth } = useAuthStore()
    const idToken = auth.idToken
    const { userData } = useUserStore()
    const email = userData.email
    const navigate = useNavigate()
    const handleOnClick = (status: DnaTestTrackingStatus) => {
        switch (status) {
            case DnaTestTrackingStatus.Delivered:
                return navigate("tutorial")
            case DnaTestTrackingStatus.TestDone:
                return navigate("register-swab-code")
            case DnaTestTrackingStatus.SampleRegistered:
                return navigate("packing")
            case DnaTestTrackingStatus.PickupRequested:
                return navigate("request-pickup")
            default:
                return
        }
    }

    const onTrackerButtonClick = async () => {
        handleOnClick(status)
        if (!email || !idToken) return
        saveEvent(
            email,
            EventType.DnaTrackerButtonClicked,
            (success) => { console.log() },
            (error) => { console.log(error) },
            "dna_test_page",
        )
        await sendEvent({
            event: NewEventType.BUTTON_CLICK,
            sub_category: "action_button",
            event_metadata: { action: "dna_widget_main_action"}
        })
    }

    const handleFullHeight = () => {
        switch (status) {
            case DnaTestTrackingStatus.Purchased:
            case DnaTestTrackingStatus.Departed:
            case DnaTestTrackingStatus.DeliveredToLaboratory:
            case DnaTestTrackingStatus.StartingToExtract:
            case DnaTestTrackingStatus.StartingAnalysis:
                return "h-[168px]"
            case DnaTestTrackingStatus.Delivered:
            case DnaTestTrackingStatus.TestDone:
            case DnaTestTrackingStatus.SampleRegistered:
            case DnaTestTrackingStatus.PickupRequested:
                return "h-[214px]"
            default:
                return ""
        }
    }
    const isMdScreen = useIsMdScreen();

    // ********************************************************************************

    return (
        <div className={`${handleFullHeight()} w-[calc(100%-4px)] rounded-[15px] self-center flex items-center justify-end shadow-platform flex-shrink-0`}>
            <div className="w-full h-[85%] flex flex-col justify-between">
                <div className="font-bold text-[12px] leading-[17px] ml-5p md:ml-[20px]">Il tuo campione genetico</div>
                <BullettedLine percentage={handlePercentage(status, isMdScreen)} status={status} />
                {
                    (status === DnaTestTrackingStatus.Purchased || status === DnaTestTrackingStatus.Departed || status === DnaTestTrackingStatus.DeliveredToLaboratory || status === DnaTestTrackingStatus.StartingToExtract || status === DnaTestTrackingStatus.StartingAnalysis) &&
                    <div className="font-normal text-[16px] leading-[24px] ml-5p md:ml-[20px]">{handleSmallBoxText(status)}</div>}
                {
                    (status === DnaTestTrackingStatus.Delivered || status === DnaTestTrackingStatus.TestDone || status === DnaTestTrackingStatus.SampleRegistered || status === DnaTestTrackingStatus.PickupRequested) &&
                    <div className="w-[90%] h-[50px] self-center md:w-[330px] md:self-start md:ml-[20px]">
                        <PrimaryButton
                            text={handleButtonTest(status)}
                            pointer={true}
                            disabled={false}
                            onClick={onTrackerButtonClick}
                        />
                    </div>
                }
            </div>
        </div>
    )
}

const handleSmallBoxText = (status: DnaTestTrackingStatus) => {
    switch (status) {
        case DnaTestTrackingStatus.Purchased:
            return "Il tuo test è in arrivo!"
        case DnaTestTrackingStatus.Departed:
            return "Il tuo campione è in viaggio verso il laboratorio."
        case DnaTestTrackingStatus.DeliveredToLaboratory:
            return "Il tuo campione è arrivato in laboratorio."
        case DnaTestTrackingStatus.StartingToExtract:
            return "È iniziata l'estrazione del tuo campione."
        case DnaTestTrackingStatus.StartingAnalysis:
            return "È iniziata l'analisi del tuo campione."
    }
}

const handleButtonTest = (status: DnaTestTrackingStatus) => {
    switch (status) {
        case DnaTestTrackingStatus.Delivered:
            return "Effettua il test e prosegui"
        case DnaTestTrackingStatus.TestDone:
            return "Registra codice campione"
        case DnaTestTrackingStatus.SampleRegistered:
            return "Richiedi il ritiro del campione"
        case DnaTestTrackingStatus.PickupRequested:
            return "Modifica il ritiro del campione"
        default:
            return ""
    }
}

const useIsMdScreen = () => {
    const [isMdScreen, setIsMdScreen] = useState(window.innerWidth >= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMdScreen(window.innerWidth >= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMdScreen;
};

const handlePercentage = (status: DnaTestTrackingStatus, isMdScreen: boolean) => {

    switch (status) {
        case DnaTestTrackingStatus.Purchased:
            return 15
        case DnaTestTrackingStatus.Delivered:
            return 22
        case DnaTestTrackingStatus.TestDone:
            return isMdScreen ? 37 : 36
        case DnaTestTrackingStatus.SampleRegistered:
            return isMdScreen ? 52 : 51
        case DnaTestTrackingStatus.PickupRequested:
            return isMdScreen ? 52 : 51
        case DnaTestTrackingStatus.Departed:
            return 57
        case DnaTestTrackingStatus.DeliveredToLaboratory:
            return 70
        case DnaTestTrackingStatus.StartingToExtract:
            return 83
        case DnaTestTrackingStatus.StartingAnalysis:
            return 94
        default:
            return 0
    }
}

export default TrackingWidget

interface BulletProps {
    percentage: number,
    status: DnaTestTrackingStatus,
}

const BullettedLine: React.FC<BulletProps> = ({
    percentage,
    status,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Track the calculated fillWidth
    const [fillWidth, setFillWidth] = useState("");

    const calculateFillWidth = () => {
        const isMd = window.matchMedia("(min-width: 768px)").matches;
        return `calc(${percentage}% - ${isMd ? 110 : 150}px)`;
    };

    // Update fillWidth on `percentage` or screen resize
    useEffect(() => {
        setFillWidth(calculateFillWidth());

        const handleResize = () => setFillWidth(calculateFillWidth());
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [percentage]);

    useEffect(() => {
        if (containerRef.current) {
            const trackerItems = containerRef.current.querySelectorAll('.tracker-item');

            let statusIndex = trackingStates.indexOf(status);
            if (statusIndex === 4) {
                statusIndex = 3
            } else if (statusIndex > 4) {
                statusIndex = statusIndex - 2
            }

            if (trackerItems[statusIndex]) {
                const targetItem = trackerItems[statusIndex] as HTMLElement;
                targetItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        }
    }, [status]);

    // ********************************************************************************

    return (
        <div className="w-full h-[55px] overflow-x-auto flex items-center relative scrollbar-hide">
            <div className="w-[1500px] h-[80%] flex justify-between relative flex-shrink-0 pl-[50px] pr-[50px] tracker-line md:w-full md:pl-[30px] md:pr-[30px]" ref={containerRef}>
                <div className="absolute top-[8px] left-[75px] right-[75px] h-[2px] bg-holifya-blue tracker-line-filled md:left-[55px] md:right-[55px]" style={{ width: fillWidth }}></div>
                <TrackerItem text="Acquisto" isTextBlue={false} dotType={DotType.Filled} />
                <TrackerItem text="Effettua il test" isTextBlue={status === DnaTestTrackingStatus.Delivered} dotType={status === DnaTestTrackingStatus.Purchased ? DotType.Normal : (status === DnaTestTrackingStatus.Delivered ? DotType.BlueBorder : DotType.Filled)} />
                <TrackerItem text="Registra campione" isTextBlue={status === DnaTestTrackingStatus.TestDone} dotType={(status === DnaTestTrackingStatus.Purchased || status === DnaTestTrackingStatus.Delivered) ? DotType.Normal : (status === DnaTestTrackingStatus.TestDone ? DotType.BlueBorder : DotType.Filled)} />
                <TrackerItem text="Richiedi il ritiro" isTextBlue={status === DnaTestTrackingStatus.SampleRegistered} dotType={(status === DnaTestTrackingStatus.Purchased || status === DnaTestTrackingStatus.Delivered || status === DnaTestTrackingStatus.TestDone) ? DotType.Normal : (status === DnaTestTrackingStatus.SampleRegistered ? DotType.BlueBorder : DotType.Filled)} />
                <TrackerItem text="In laboratiorio" isTextBlue={false} dotType={trackingStates.indexOf(status) >= trackingStates.indexOf(DnaTestTrackingStatus.DeliveredToLaboratory) ? DotType.Filled : DotType.Normal} />
                <TrackerItem text="Inizio estrazione" isTextBlue={false} dotType={trackingStates.indexOf(status) >= trackingStates.indexOf(DnaTestTrackingStatus.StartingToExtract) ? DotType.Filled : DotType.Normal} />
                <TrackerItem text="Analisi" isTextBlue={false} dotType={trackingStates.indexOf(status) >= trackingStates.indexOf(DnaTestTrackingStatus.StartingAnalysis) ? DotType.Filled : DotType.Normal} />
                <TrackerItem text="Risultato" isTextBlue={false} dotType={DotType.Normal} />
            </div>
        </div>
    )
}

interface ItemProps {
    text: string,
    isTextBlue: boolean,
    dotType: DotType,
}

const TrackerItem: React.FC<ItemProps> = ({
    text,
    isTextBlue,
    dotType,
}) => {
    return (
        <div className="flex flex-col items-center justify-between tracker-item">
            <div className={`w-[16px] h-[16px] rounded-[8px] z-[10] ${handleDotType(dotType)}`} />
            <div className={`font-bold text-[14px] leading-[20px] ${isTextBlue && "text-holifya-blue"}`}>{text}</div>
        </div>
    )
}

const handleDotType = (dotType: DotType) => {
    switch (dotType) {
        case DotType.Filled:
            return "bg-holifya-blue border-2 border-solid border-holifya-blue"
        case DotType.BlueBorder:
            return "bg-white border-2 border-solid border-holifya-blue"
        case DotType.Normal:
            return "bg-white border-2 border-solid border-holifya-light-blue"
    }
}

enum DotType {
    Filled = "filled",
    BlueBorder = "blue-border",
    Normal = "normal",
}

const trackingStates = [
    DnaTestTrackingStatus.Purchased,
    DnaTestTrackingStatus.Delivered,
    DnaTestTrackingStatus.TestDone,
    DnaTestTrackingStatus.SampleRegistered,
    DnaTestTrackingStatus.PickupRequested,
    DnaTestTrackingStatus.Departed,
    DnaTestTrackingStatus.DeliveredToLaboratory,
    DnaTestTrackingStatus.StartingToExtract,
    DnaTestTrackingStatus.StartingAnalysis,
    DnaTestTrackingStatus.AnalysisDone,
    DnaTestTrackingStatus.ReportGenerated,
]
